import React from 'react';
import { useFilteredRequests } from '../../utils/hooks/use-filtered-requests';
import { useRecoilValue } from 'recoil';
import { blockExportMetadata, blockMetrics, isReportState, recoilBlockContentSize } from 'venn-state';
import { PairwiseCorrelation } from './pairwise-correlation';
import { useAppPrintMode } from '../../../print';
import { PortfolioCorrelation } from './portfolio-correlation';
import { useSetBlockSize } from '../../../studio-blocks/logic/useSetBlockSize';
import Measure from 'react-measure';
import isNil from 'lodash/isNil';
import { cn } from '@/lib/utils';

type SupportedMetrics =
  | 'pairwiseCorrelation'
  | 'portfolioCorrelation'
  | 'residualPortfolioCorrelation'
  | 'pairwiseDownsideCorrelation'
  | 'portfolioDownsideCorrelation';

export const CorrelationResultMap: Record<SupportedMetrics, number> = {
  pairwiseCorrelation: 0,
  portfolioCorrelation: 1,
  residualPortfolioCorrelation: 2,
  pairwiseDownsideCorrelation: 3,
  portfolioDownsideCorrelation: 4,
};

export const CorrelationContent = ({ id }: { id: string }) => {
  const { requests, analyses } = useFilteredRequests(id);
  const selectedMetricIds = useRecoilValue(blockMetrics(id));
  const { inPrintModeOrReportLab } = useAppPrintMode();
  const exportMetaData = useRecoilValue(blockExportMetadata(id));
  const isReport = useRecoilValue(isReportState);
  const metric = selectedMetricIds[0];
  // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
  const analysis = !isNil(metric) ? analyses[CorrelationResultMap[metric]]?.[0] : undefined;

  switch (metric) {
    case 'pairwiseCorrelation':
    case 'pairwiseDownsideCorrelation':
      return (
        <Wrapper id={id}>
          <PairwiseCorrelation
            id={id}
            requests={requests}
            exportMetaData={exportMetaData}
            inPrintMode={inPrintModeOrReportLab}
            isReport={isReport}
            analysis={analysis}
          />
        </Wrapper>
      );
    case 'portfolioCorrelation':
    case 'residualPortfolioCorrelation':
    case 'portfolioDownsideCorrelation':
      return (
        <Wrapper id={id}>
          <PortfolioCorrelation
            id={id}
            requests={requests}
            exportMetaData={exportMetaData}
            inPrintMode={inPrintModeOrReportLab}
            isReport={isReport}
            analysis={analysis}
          />
        </Wrapper>
      );
    default:
      return null;
  }
};

const Wrapper = ({ id, children }: React.PropsWithChildren<{ id: string }>) => {
  const { inPrintModeOrReportLab } = useAppPrintMode();
  const onResize = useSetBlockSize(recoilBlockContentSize.transformedState(id));

  return (
    <Measure bounds onResize={onResize}>
      {({ measureRef }) => (
        <div className={cn('inline-block', { 'overflow-x-scroll': !inPrintModeOrReportLab })}>
          <div className="inline-block min-w-full" ref={measureRef}>
            {children}
          </div>
        </div>
      )}
    </Measure>
  );
};
